import { createStyles, Loader } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import Modal from '~/domains/common/components/Modal/Modal/Modal';
import { Button } from '~/domains/design-system/Button';
import DrawerTitle from '~/domains/design-system/components/Drawer/DrawerTitle';
import { textStyles } from '~/domains/design-system/Text';
import { TextInput } from '~/domains/design-system/TextInput';
import { ENTRY_NAME_REGEX } from '~/domains/entrants-manager/schemas/entrySchema';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._16,
  },
  drawerBody: {
    paddingTop: theme.other.spacing._16,
  },
  error: {
    ...textStyles['body-medium'],
    color: theme.colors.orange[3],
  },
  loader: {
    verticalAlign: 'bottom',
  },
  root: {
    zIndex: 201,
  },
}));

const getNameSchema = (t: ReturnType<typeof useTranslation<'contest'>>['t']) =>
  z
    .string()
    .min(1, { message: t('gamecenter.editNameEditModal.errors.min') })
    .max(30, { message: t('gamecenter.editNameEditModal.errors.max') })
    .regex(ENTRY_NAME_REGEX, {
      message: t('gamecenter.editNameEditModal.errors.regex'),
    });

function EditEntryNameModal({
  defaultEntryName,
  isOpened,
  isLoading,
  error,
  onClose,
  onSubmit,
}: {
  defaultEntryName: string;
  isOpened: boolean;
  onClose: () => void;
  onSubmit: (newName: string) => Promise<void>;
  isLoading?: boolean;
  error?: string;
}) {
  const { classes } = useStyles();
  const [entryName, setEntryName] = useState(defaultEntryName);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isPristine, setIsPristine] = useState(true);
  const { t } = useTranslation('contest');
  const nameSchema = useMemo(() => getNameSchema(t), [t]);

  useEffect(() => {
    setEntryName(defaultEntryName);
  }, [defaultEntryName]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value = '' } = e.target;
      setEntryName(value);
      setIsPristine(false);
      const result = nameSchema.safeParse(value);
      // eslint-disable-next-line local-rules/require-let-annotation
      let errorMessage = null;
      if (result.success === false) {
        errorMessage = result.error.errors[0]?.message;
      }

      setFieldError(errorMessage);
    },
    [nameSchema]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isPristine || error) {
        return;
      }

      onSubmit(entryName)
        .then(() => onClose())
        .catch(() => {});
    },
    [isPristine, error, onSubmit, entryName, onClose]
  );

  useLayoutEffect(() => {
    if (!isOpened) {
      return;
    }
    // Apparently modal is messing up with focus on open, so we need to wait a bit
    setTimeout(() => {
      document.getElementById('entry-name')?.focus();
    }, 100);
  }, [isOpened]);

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      classNames={{
        drawer: { body: classes.drawerBody, root: classes.root },
        modal: { root: classes.root },
      }}
    >
      <form className={classes.container} onSubmit={handleSubmit}>
        <DrawerTitle>{t('gamecenter.editNameEditModal.title')}</DrawerTitle>
        {error && <div className={classes.error}>{error}</div>}
        <TextInput
          label={t('gamecenter.editNameEditModal.field')}
          value={entryName}
          id="entry-name"
          hasBorder
          onChange={handleChange}
          error={fieldError}
          disabled={isLoading}
        />
        <Button
          variant="primary-fill"
          size="medium"
          disabled={!!fieldError || isPristine || isLoading}
          type="submit"
        >
          {isLoading ? (
            <Loader color="black" size={24} className={classes.loader} />
          ) : (
            t('gamecenter.editNameEditModal.submit')
          )}
        </Button>
      </form>
    </Modal>
  );
}

export default EditEntryNameModal;
